import React, { useState, useMemo, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import { 
    FaEdit, FaTrashAlt, FaTimes, FaUpload, 
    FaSearch, FaChevronDown, FaChevronUp, FaPlus, 
    FaExchangeAlt, FaSave, FaBold, FaItalic, FaUnderline, 
    FaListUl, FaListOl, FaHeading, FaImage, FaParagraph, 
    FaLink, FaCalendarAlt
} from 'react-icons/fa';
import { Helmet } from "react-helmet-async";
import './AddNews.css';
import axiosInstance from '../../api/axiosInstance';
import Pagination from '../Pagination/Pagination';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://localhost:5000";
const formatPublishDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '-';
const formatDateTimeString = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

// --- CUSTOM RICH TEXT EDITOR BLOCK ---
const TextEditorBlock = ({ block, onChange }) => {
    const editorRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (editorRef.current && !isFocused && block.content !== editorRef.current.innerHTML) {
            editorRef.current.innerHTML = block.content;
        }
    }, [block.content, isFocused]);

    const formatDoc = (cmd, value = null) => {
        document.execCommand(cmd, false, value);
        editorRef.current.focus();
        onChange(editorRef.current.innerHTML);
    };

    return (
        <div className="admin-add-news-editor-container">
            <div className="admin-add-news-editor-toolbar">
                <button title="Bold" onClick={() => formatDoc('bold')}><FaBold /></button>
                <button title="Italic" onClick={() => formatDoc('italic')}><FaItalic /></button>
                <button title="Underline" onClick={() => formatDoc('underline')}><FaUnderline /></button>
                <div className="admin-add-news-toolbar-divider"></div>
                <button title="Heading 1" onClick={() => formatDoc('formatBlock', 'H1')}><FaHeading />1</button>
                <button title="Heading 2" onClick={() => formatDoc('formatBlock', 'H2')}><FaHeading />2</button>
                <button title="Paragraph" onClick={() => formatDoc('formatBlock', 'P')}><FaParagraph /></button>
                <div className="admin-add-news-toolbar-divider"></div>
                <button title="Unordered List" onClick={() => formatDoc('insertUnorderedList')}><FaListUl /></button>
                <button title="Ordered List" onClick={() => formatDoc('insertOrderedList')}><FaListOl /></button>
            </div>
            <div 
                className="admin-add-news-editor-content" 
                contentEditable={true} 
                suppressContentEditableWarning={true}
                ref={editorRef}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onInput={(e) => onChange(e.currentTarget.innerHTML)}
            />
        </div>
    );
};

// --- MULTIPLE IMAGE GALLERY BLOCK ---
const ImageGalleryBlock = ({ block, onChange }) => {
    const existingImages = block.imageUrls || [];
    const newFiles = block.files || [];

    const handleFiles = (e) => {
        const files = Array.from(e.target.files);
        onChange({ files: [...newFiles, ...files], imageUrls: existingImages });
    };

    const removeExistingImage = (index) => {
        const updated = [...existingImages];
        updated.splice(index, 1);
        onChange({ files: newFiles, imageUrls: updated });
    };

    const removeNewFile = (index) => {
        const updated = [...newFiles];
        updated.splice(index, 1);
        onChange({ files: updated, imageUrls: existingImages });
    };

    const totalCount = existingImages.length + newFiles.length;

    return (
        <div className="admin-add-news-gallery-container">
            <div className="admin-add-news-gallery-upload-zone">
                <input type="file" multiple accept="image/*" onChange={handleFiles} id={`gallery-upload-${block.id}`} hidden />
                <label htmlFor={`gallery-upload-${block.id}`} className="admin-add-news-upload-label">
                    <FaUpload />
                    <span>Click to select multiple images</span>
                </label>
            </div>
            {totalCount > 0 && (
                <div className="admin-add-news-gallery-preview" data-count={totalCount}>
                    {existingImages.map((img, idx) => (
                        <div key={`old-${idx}`} className="admin-add-news-gallery-item">
                            <img src={`${API_BASE_URL}${img}`} alt="" />
                            <button type="button" className="admin-add-news-gallery-remove" onClick={() => removeExistingImage(idx)}><FaTimes /></button>
                        </div>
                    ))}
                    {newFiles.map((file, idx) => (
                        <div key={`new-${idx}`} className="admin-add-news-gallery-item">
                            <img src={URL.createObjectURL(file)} alt="" />
                            <button type="button" className="admin-add-news-gallery-remove" onClick={() => removeNewFile(idx)}><FaTimes /></button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const initialFormState = { title: '', slug: '', publishDate: '', isActive: true };

// --- MAIN COMPONENT ---
const AddNews = () => {
    const [newsData, setNewsData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRow, setExpandedRow] = useState(null); 
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Inline Form States
    const [showAddForm, setShowAddForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [slugEdited, setSlugEdited] = useState(false);
    const [coverImageFile, setCoverImageFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState('');
    const [removeCoverImage, setRemoveCoverImage] = useState(false);
    const [blocks, setBlocks] = useState([{ id: Date.now(), type: 'text', content: '', files: [] }]);
    const coverInputRef = useRef(null);

    const fetchNews = async () => {
        try {
            const res = await axiosInstance.get('/api/news');
            setNewsData(res.data);
        } catch (error) { 
            console.error("Error fetching news:", error); 
        }
    };

    useEffect(() => { fetchNews(); }, []);

    const filteredData = useMemo(() => newsData.filter(item => 
        item.title.toLowerCase().includes(searchTerm.toLowerCase())
    ), [newsData, searchTerm]);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredData, currentPage, itemsPerPage]);

    useEffect(() => { setCurrentPage(1); }, [searchTerm]);

    const handleTitleChange = (e) => {
        const title = e.target.value;
        let newSlug = formData.slug;
        if (!slugEdited) newSlug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
        setFormData({ ...formData, title, slug: newSlug });
    };

    const handleCoverChange = (e) => { 
        const file = e.target.files[0]; 
        if (file) { 
            setCoverImageFile(file); 
            setCoverPreview(URL.createObjectURL(file)); 
            setRemoveCoverImage(false);
        } 
    };

    const addBlock = (type) => setBlocks([...blocks, { id: Date.now(), type, content: '', files: [] }]);
    
    const updateBlock = (id, field, value) => {
        setBlocks((prev) => prev.map((block) => {
            if (block.id !== id) return block;
            if (typeof value === "object") return { ...block, ...value };
            return { ...block, [field]: value };
        }));
    };
    
    const removeBlock = (id) => setBlocks(blocks.filter(b => b.id !== id));
    
    const moveBlock = (index, dir) => {
        if (index + dir < 0 || index + dir >= blocks.length) return;
        const newBlocks = [...blocks];
        [newBlocks[index], newBlocks[index + dir]] = [newBlocks[index + dir], newBlocks[index]];
        setBlocks(newBlocks);
    };

    const handleSave = async () => {
        if (!formData.title) return Swal.fire('Error', 'Please enter news title', 'error');
        if (!formData.slug) return Swal.fire('Error', 'Please enter a slug URL', 'error');
        if (!formData.publishDate) return Swal.fire('Error', 'Please select publish date', 'error');

        // Extract and validate that at least one text block exists
        const textBlocks = blocks.filter(b => b.type === 'text');
        if (textBlocks.length === 0) {
            return Swal.fire('Error', 'At least one text block is required.', 'error');
        }

        // Validate that at least one of those text blocks contains actual text data
        const hasValidText = textBlocks.some(b => {
            if (!b.content) return false;
            const stripped = b.content
                .replace(/<[^>]*>?/gm, '') // Remove HTML tags
                .replace(/&nbsp;/g, ' ')   // Convert non-breaking space code
                .trim();
            return stripped.length > 0;
        });

        if (!hasValidText) {
            return Swal.fire('Error', 'At least one text block with written content is required.', 'error');
        }
        
        const submissionData = new FormData();
        submissionData.append('title', formData.title); 
        submissionData.append('slug', formData.slug); 
        submissionData.append('publishDate', formData.publishDate); 
        submissionData.append('isActive', formData.isActive);
        submissionData.append('removeCoverImage', removeCoverImage.toString());
        
        if (coverImageFile) submissionData.append('image', coverImageFile);

        const cleanedBlocks = blocks.map((b) => ({
            id: b.id, type: b.type,
            content: b.type === "text" ? b.content : "",
            imageUrls: b.type === "image" ? (b.imageUrls || []) : []
        }));

        submissionData.append('contentBlocks', JSON.stringify(cleanedBlocks));
        
        blocks.forEach((b) => {
            if (b.type === 'image' && b.files) {
                b.files.forEach((f) => submissionData.append(`block_${b.id}_images`, f));
            }
        });

        const url = editId ? `/api/news/${editId}` : `/api/news`;
        const method = editId ? 'put' : 'post';

        try {
            await axiosInstance({ method, url, data: submissionData });
            await fetchNews();
            Swal.fire(editId ? 'Updated!' : 'Published!', '', 'success');
            resetForm();
        } catch (error) {
            const errMsg = error.response?.data?.message || 'Action failed! Please try again.';
            Swal.fire('Error', errMsg, 'error');
        }
    };

    const handleEdit = (data) => {
        setFormData({ 
            title: data.title, 
            slug: data.slug || '', 
            publishDate: data.publishDate ? data.publishDate.split('T')[0] : '', 
            isActive: data.isActive !== false 
        });
        setCoverPreview(data.image ? `${API_BASE_URL}${data.image}` : '');
        setRemoveCoverImage(false);
        setCoverImageFile(null);

        if (data.contentBlocks) {
            try { 
                setBlocks(JSON.parse(data.contentBlocks)); 
            } catch(e) { 
                setBlocks([{ id: Date.now(), type: 'text', content: data.content || '', files: [] }]); 
            }
        } else {
            setBlocks([{ id: Date.now(), type: 'text', content: data.content || '', files: [] }]);
        }
        setSlugEdited(true);
        setEditId(data._id);
        setShowAddForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' }); 
    };

    const handleDelete = (id) => {
        Swal.fire({ title: 'Delete News?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e74c3c' }).then(async res => {
            if (res.isConfirmed) {
                try {
                    await axiosInstance.delete(`/api/news/${id}`);
                    await fetchNews();
                    Swal.fire('Deleted', '', 'success');
                } catch (error) {
                    Swal.fire('Error', 'Could not delete news.', 'error');
                }
            }
        });
    };

    const toggleStatus = async (id, currentStatus) => {
        try {
            await axiosInstance.put(`/api/news/${id}/status`, { isActive: !currentStatus });
            fetchNews();
        } catch (error) {
            Swal.fire('Error', 'Failed to update status', 'error');
        }
    };

    const resetForm = () => {
        setShowAddForm(false);
        setFormData(initialFormState);
        setBlocks([{ id: Date.now(), type: 'text', content: '', files: [] }]);
        setCoverPreview('');
        setCoverImageFile(null);
        setRemoveCoverImage(false);
        setSlugEdited(false);
        setEditId(null);
    };

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));

    return (
        <>
        <Helmet>
                    <title>Add News | Rajput Shiksha Kosh</title>
            
                    <meta
                      name="description"
                      content="Manage news posts for the Rajput Shiksha Kosh organization."
                    />
                  </Helmet>
        <div className="admin-add-news-container">
            <h2 className="admin-add-news-title"><FaCalendarAlt style={{marginRight: '8px'}}/> News Post Manager</h2>
            
            <div className="admin-add-news-topbar">
                <div className="admin-add-news-topbar-left">
                    <div className="admin-add-news-search-wrapper">
                        <FaSearch className="admin-add-news-search-icon" />
                        <input
                            type="text"
                            placeholder="Search news titles..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="admin-add-news-search-input"
                        />
                    </div>
                </div>

                <div className="admin-add-news-topbar-right">
                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="admin-add-news-items-select"
                    >
                        <option value="5">5 / page</option>
                        <option value="10">10 / page</option>
                        <option value="20">20 / page</option>
                        <option value="30">30 / page</option>
                        <option value="50">50 / page</option>
                    </select>

                    <button
                        className="admin-add-news-btn admin-add-news-btn-add"
                        onClick={() => showAddForm ? resetForm() : setShowAddForm(true)}
                    >
                        {showAddForm ? <><FaTimes /> Close Form</> : <><FaPlus /> Create News Post</>}
                    </button>
                </div>
            </div>

            {showAddForm && (
                <div className="admin-add-news-inline-form">
                    <h3 className="admin-add-news-form-title">{editId ? '✏️ Edit News Layout' : 'Create News Layout'}</h3>
                    
                    <div className="admin-add-news-layout-grid">
                        <div className="admin-add-news-sidebar">
                            <h4 className="admin-add-news-section-title">Post Settings</h4>
                            
                            <div className="admin-add-news-form-group">
                                <label>Title Image (Cover)</label>
                                <div className="admin-add-news-square-preview" onClick={() => coverInputRef.current.click()}>
                                    {coverPreview ? (
                                        <div className="admin-add-news-image-container">
                                            <img src={coverPreview} alt="Cover" />
                                            <div className="admin-add-news-overlay">
                                                <button type="button" className="admin-add-news-mini-btn" onClick={(e) => {e.stopPropagation(); coverInputRef.current.click();}}><FaExchangeAlt /></button>
                                                <button type="button" className="admin-add-news-mini-btn remove" onClick={(e) => { e.stopPropagation(); setCoverImageFile(null); setCoverPreview(''); setRemoveCoverImage(true);}}><FaTrashAlt /></button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="admin-add-news-upload-placeholder">
                                            <FaUpload size={24}/>
                                            <span>Upload Cover</span>
                                        </div>
                                    )}
                                    <input type="file" ref={coverInputRef} hidden onChange={handleCoverChange} accept="image/*" />
                                </div>
                            </div>

                            <div className="admin-add-news-form-group">
                                <label>News Title <span className="admin-add-news-req">*</span></label>
                                <input type="text" value={formData.title} onChange={handleTitleChange} placeholder="Enter headline..." />
                            </div>
                            
                            <div className="admin-add-news-form-group">
                                <label>Slug (URL) <span className="admin-add-news-req">*</span></label>
                                <input type="text" value={formData.slug} onChange={e => {setSlugEdited(true); setFormData({...formData, slug: e.target.value});}} style={{fontFamily: 'monospace', color: '#8b5cf6'}} placeholder="auto-generated-slug"/>
                            </div>
                            
                            <div className="admin-add-news-form-group">
                                <label>Publish Date <span className="admin-add-news-req">*</span></label>
                                <input type="date" value={formData.publishDate} onChange={(e)=>setFormData({...formData, publishDate: e.target.value})} />
                            </div>
                        </div>

                        <div className="admin-add-news-main-area">
                            <h4 className="admin-add-news-section-title">Content Blocks <span className="admin-add-news-req">*</span></h4>
                            <p className="admin-add-news-hint">Add text and image blocks to build your layout. <strong>At least one text block with written content is required.</strong></p>
                            
                            <div className="admin-add-news-blocks-wrapper">
                                {blocks.map((block, idx) => (
                                    <div key={block.id} className="admin-add-news-block-card">
                                        <div className="admin-add-news-block-header">
                                            <span className="admin-add-news-block-badge">
                                                {block.type === 'text' ? <FaParagraph /> : <FaImage />} {block.type.toUpperCase()}
                                            </span>
                                            <div className="admin-add-news-block-actions">
                                                <button type="button" onClick={() => moveBlock(idx, -1)} disabled={idx === 0}><FaChevronUp /></button>
                                                <button type="button" onClick={() => moveBlock(idx, 1)} disabled={idx === blocks.length - 1}><FaChevronDown /></button>
                                                <button type="button" onClick={() => removeBlock(block.id)} className="delete"><FaTrashAlt /></button>
                                            </div>
                                        </div>
                                        <div className="admin-add-news-block-body">
                                            {block.type === 'text' && <TextEditorBlock block={block} onChange={(val) => updateBlock(block.id, 'content', val)} />}
                                            {block.type === 'image' && <ImageGalleryBlock block={block} onChange={(val) => updateBlock(block.id, null, val)} />}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="admin-add-news-add-blocks-row">
                                <button type="button" className="admin-add-news-add-block-btn text" onClick={() => addBlock('text')}>+ Add Text Block</button>
                                <button type="button" className="admin-add-news-add-block-btn image" onClick={() => addBlock('image')}>+ Add Image Gallery</button>
                            </div>
                        </div>
                    </div>

                    <div className="admin-add-news-form-buttons">
                        <button type="button" className="admin-add-news-btn-cancel" onClick={resetForm}>Cancel</button>
                        <button type="button" className="admin-add-news-btn-confirm" onClick={handleSave}><FaSave style={{marginRight: '5px'}}/> {editId ? 'Update News' : 'Publish News'}</button>
                    </div>
                </div>
            )}

            <div className="admin-add-news-table-wrapper">
                <table className="admin-add-news-table">
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th className="admin-add-news-col-img">Image</th>
                            <th className="admin-add-news-align-left">News Title</th>
                            <th className="admin-add-news-col-slug">Slug</th>
                            <th className="admin-add-news-col-date">Publish Date</th>
                            <th className="admin-add-news-col-status">Status</th>
                            <th className="admin-add-news-col-action">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length === 0 ? (
                            <tr><td colSpan="7" className="admin-add-news-empty-state">No news posts found.</td></tr>
                        ) : paginatedData.map((item, index) => (
                            <React.Fragment key={item._id}>
                                <tr className="admin-add-news-main-row">
                                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    
                                    <td className="admin-add-news-col-img">
                                        {item.image ? (
                                            <img src={`${API_BASE_URL}${item.image}`} className="admin-add-news-table-thumb" alt="cover"/>
                                        ) : (
                                            <span className="admin-add-news-no-img">No Cover</span>
                                        )}
                                    </td>

                                    <td className="admin-add-news-align-left">
                                        <div className="admin-add-news-name-wrapper" onClick={() => toggleRow(item._id)}>
                                            <span className="admin-add-news-text-bold">{item.title}</span>
                                            <button className="admin-add-news-expand-btn">
                                                {expandedRow === item._id ? <FaChevronUp /> : <FaChevronDown />}
                                            </button>
                                        </div>
                                    </td>

                                    <td className="admin-add-news-col-slug">
                                        <div className="admin-add-news-slug-badge"><FaLink className="slug-icon"/> {item.slug}</div>
                                    </td>

                                    <td className="admin-add-news-col-date">{formatPublishDate(item.publishDate)}</td>
                                    
                                    <td className="admin-add-news-col-status">
                                        <label className="admin-add-news-switch">
                                            <input type="checkbox" checked={item.isActive !== false} onChange={() => toggleStatus(item._id, item.isActive !== false)} />
                                            <span className="admin-add-news-slider" />
                                        </label>
                                        <div className={`admin-add-news-status-label-text ${item.isActive !== false ? 'active' : 'disabled'}`}>
                                            {item.isActive !== false ? 'Active' : 'Draft'}
                                        </div>
                                    </td>

                                    <td className="admin-add-news-col-action">
                                        <div className="admin-add-news-action-btns">
                                            <button className="admin-add-news-btn-edit" onClick={() => handleEdit(item)} title="Edit"><FaEdit /></button>
                                            <button className="admin-add-news-btn-delete" onClick={() => handleDelete(item._id)} title="Delete"><FaTrashAlt /></button>
                                        </div>
                                    </td>
                                </tr>
                                
                                {expandedRow === item._id && (
                                    <tr className="admin-add-news-expanded-row">
                                        <td colSpan="7">
                                            <div className="admin-add-news-expanded-content">
                                                <div className="admin-add-news-exp-item admin-add-news-exp-img">
                                                    <span className="admin-add-news-exp-label">Cover Image</span>
                                                    <span className="admin-add-news-exp-value">
                                                        {item.image ? <img src={`${API_BASE_URL}${item.image}`} className="admin-add-news-table-thumb-expanded" alt="cover"/> : 'No Image'}
                                                    </span>
                                                </div>
                                                <div className="admin-add-news-exp-item admin-add-news-exp-slug">
                                                    <span className="admin-add-news-exp-label">Slug URL</span>
                                                    <span className="admin-add-news-exp-value admin-add-news-slug-badge"><FaLink className="slug-icon"/> {item.slug}</span>
                                                </div>
                                                <div className="admin-add-news-exp-item admin-add-news-exp-date">
                                                    <span className="admin-add-news-exp-label">Publish Date</span>
                                                    <span className="admin-add-news-exp-value">{formatPublishDate(item.publishDate)}</span>
                                                </div>
                                                <div className="admin-add-news-exp-item admin-add-news-exp-status">
                                                    <span className="admin-add-news-exp-label">Status</span>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                                        <label className="admin-add-news-switch">
                                                            <input type="checkbox" checked={item.isActive !== false} onChange={() => toggleStatus(item._id, item.isActive !== false)} />
                                                            <span className="admin-add-news-slider" />
                                                        </label>
                                                        <span className={`admin-add-news-status-label-text ${item.isActive !== false ? 'active' : 'disabled'}`}>
                                                            {item.isActive !== false ? 'Active' : 'Draft'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="admin-add-news-exp-item admin-add-news-exp-action">
                                                    <span className="admin-add-news-exp-label">Actions</span>
                                                    <div className="admin-add-news-action-btns" style={{justifyContent: 'flex-start', marginTop: '4px'}}>
                                                        <button className="admin-add-news-btn-edit" onClick={() => handleEdit(item)}><FaEdit/> Edit</button>
                                                        <button className="admin-add-news-btn-delete" onClick={() => handleDelete(item._id)}><FaTrashAlt/> Delete</button>
                                                    </div>
                                                </div>
                                                <div className="admin-add-news-exp-item">
                                                    <span className="admin-add-news-exp-label">Created On</span>
                                                    <span className="admin-add-news-exp-value">{formatDateTimeString(item.createdAt)}</span>
                                                </div>
                                                <div className="admin-add-news-exp-item">
                                                    <span className="admin-add-news-exp-label">Last Updated</span>
                                                    <span className="admin-add-news-exp-value">{formatDateTimeString(item.updatedAt)}</span>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="admin-add-news-pagination-wrapper">
                <Pagination 
                    totalItems={filteredData.length}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                />
            </div>
        </div>
        </>
    );
};

export default AddNews;